import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Collapse} from 'views/components';
import {Translate, translate} from 'views/module';

const WorkAvailabilitySection = (props) => {
  const {onAvailabilityChange, availability = {}, setValidity} = props;

  // Drag state
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartValue, setDragStartValue] = React.useState(false);

  // Constants
  const weekdays = [
    translate('caregiverScreening.WorkAvailability.Weekdays.Monday'),
    translate('caregiverScreening.WorkAvailability.Weekdays.Tuesday'),
    translate('caregiverScreening.WorkAvailability.Weekdays.Wednesday'),
    translate('caregiverScreening.WorkAvailability.Weekdays.Thursday'),
    translate('caregiverScreening.WorkAvailability.Weekdays.Friday'),
    translate('caregiverScreening.WorkAvailability.Weekdays.Saturday'),
    translate('caregiverScreening.WorkAvailability.Weekdays.Sunday'),
  ];
  const timePeriods = {
    morning: {
      key: 'morning',
      label: translate('caregiverScreening.WorkAvailability.TimePeriods.Morning.Label'),
      subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Morning.Subtitle'),
      hours: 6,
    },
    afternoon: {
      key: 'afternoon',
      label: translate('caregiverScreening.WorkAvailability.TimePeriods.Afternoon.Label'),
      subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Afternoon.Subtitle'),
      hours: 5,
    },
    evening: {
      key: 'evening',
      label: translate('caregiverScreening.WorkAvailability.TimePeriods.Evening.Label'),
      subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Evening.Subtitle'),
      hours: 4,
    },
    night: {
      key: 'night',
      label: translate('caregiverScreening.WorkAvailability.TimePeriods.Night.Label'),
      subtitle: translate('caregiverScreening.WorkAvailability.TimePeriods.Night.Subtitle'),
      hours: 9,
    },
  };

  // Helper functions
  const getDayKey = (day) => `${day.toLowerCase()}Shifts`;

  const isCellSelected = (day, period) => {
    const dayKey = getDayKey(day);
    const dayShifts = availability[dayKey] || [];
    return dayShifts.includes(period.key);
  };

  const updateAvailability = React.useCallback((day, period, isSelected) => {
    const dayKey = getDayKey(day);
    const currentShifts = availability[dayKey] || [];

    let updatedShifts;
    if (isSelected) {
      // Add shift if not already present
      updatedShifts = currentShifts.includes(period.key) ?
        currentShifts :
        [...currentShifts, period.key];
    } else {
      // Remove shift
      updatedShifts = currentShifts.filter((shift) => shift !== period.key);
    }

    const updatedAvailability = {
      ...availability,
      [dayKey]: updatedShifts,
    };

    if (onAvailabilityChange) {
      onAvailabilityChange(updatedAvailability);
    }
  }, [availability, onAvailabilityChange]);

  // Event handlers
  const handleCellMouseDown = (day, period) => {
    const currentlySelected = isCellSelected(day, period);
    const newValue = !currentlySelected;

    setIsDragging(true);
    setDragStartValue(newValue);
    updateAvailability(day, period, newValue);
  };

  const handleCellMouseEnter = (day, period) => {
    if (isDragging) {
      updateAvailability(day, period, dragStartValue);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };


  // Count selected slots for summary
  const selectedCount = Object.values(availability).reduce((total, dayShifts) =>
    total + (Array.isArray(dayShifts) ? dayShifts.length : 0), 0,
  );
  const totalSlots = weekdays.length * Object.keys(timePeriods).length;

  // Update validity when selectedCount changes
  React.useEffect(() => {
    if (setValidity) {
      setValidity(selectedCount > 0);
    }
  }, [selectedCount, setValidity]);

  // Calculate total hours directly from availability
  const totalPossibleHours = Object.values(availability).reduce((sum, dayShifts) => {
    if (Array.isArray(dayShifts)) {
      return sum + dayShifts.reduce((daySum, periodKey) =>
        daySum + (timePeriods[periodKey]?.hours || 0), 0,
      );
    }
    return sum;
  }, 0);

  return (
    <Box
      sx={{p: {md: 3, xs: 1}}}
      onMouseUp={handleMouseUp}
    >
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.WorkAvailability.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverScreening.WorkAvailability.Subtitle'/>
      </Typography>

      {/* Container for grid and summary */}
      <Box
        sx={{
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        {/* Availability Grid */}
        <Box
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            userSelect: 'none',
          }}
        >
          {/* Header row with time periods */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                md: '120px repeat(4, 1fr)',
                xs: '80px repeat(4, 1fr)',
              },
              gap: 1,
              mb: 1,
            }}
          >
            <Box sx={{p: 1}}>
              {/* Empty corner cell */}
            </Box>
            {
              Object.values(timePeriods).map((period) => (
                <Box
                  key={period.key}
                  sx={{
                    p: 1,
                    textAlign: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      display: 'block',
                    }}
                  >
                    {period.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'wrap',
                      display: 'block',
                    }}
                  >
                    ({period.subtitle})
                  </Typography>
                </Box>
              ))
            }
          </Box>

          {/* Grid rows for each weekday */}
          {
            weekdays.map((day) => (
              <Box
                key={day}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    md: '120px repeat(4, 1fr)',
                    xs: '80px repeat(4, 1fr)',
                  },
                  gap: 1,
                  mb: 1,
                }}
              >
                {/* Day label */}
                <Box
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {day}
                  </Typography>
                </Box>

                {/* Time period cells */}
                {
                  Object.values(timePeriods).map((period) => {
                    const isSelected = isCellSelected(day, period);
                    return (
                      <Box
                        key={`${day}-${period.key}`}
                        sx={{
                          'p': 1,
                          'minHeight': 40,
                          'border': 1,
                          'borderColor': isSelected ? 'primary.main' : 'divider',
                          'borderRadius': 1,
                          'backgroundColor': isSelected ? 'primary.main' : 'background.paper',
                          'cursor': 'pointer',
                          'transition': 'all 0.2s ease-in-out',
                          'display': 'flex',
                          'alignItems': 'center',
                          'justifyContent': 'center',
                          '&:hover': {
                            backgroundColor: isSelected ?
                          'primary.dark' :
                          'action.hover',
                            borderColor: isSelected ?
                          'primary.dark' :
                          'primary.light',
                          },
                        }}
                        onMouseDown={() => handleCellMouseDown(day, period)}
                        onMouseEnter={() => handleCellMouseEnter(day, period)}
                      />
                    );
                  })
                }
              </Box>
            ))
          }
        </Box>

        {/* Summary */}
        <Collapse in={selectedCount > 0}>
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="body1" color="primary.main" fontWeight={500} sx={{mb: 2}}>
              <Translate expression='caregiverScreening.WorkAvailability.Summary.SlotsSelected' substitute={{selected: selectedCount, total: totalSlots}}/>
            </Typography>

            {/* Daily breakdown */}
            <Box sx={{mt: 2}}>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                <Translate expression='caregiverScreening.WorkAvailability.Summary.DailyAvailability'/>
              </Typography>
              {
                ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                    .map((dayKey) => {
                      const dayShiftsKey = `${dayKey}Shifts`;
                      const dayShifts = availability[dayShiftsKey];

                      if (!Array.isArray(dayShifts) || dayShifts.length === 0) {
                        return null;
                      }

                      const day = dayKey.replace(/^\w/, (c) => c.toUpperCase());
                      const slots = dayShifts.map((periodKey) => timePeriods[periodKey]);
                      const totalHours = slots.reduce((sum, slot) => sum + slot.hours, 0);

                      return (
                        <Box
                          key={day}
                          sx={{
                            mb: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="body2">
                            <Typography component="span" fontWeight={600}>
                              {day}:
                            </Typography>
                            {' '}{slots.map((slot) => slot.label).join(', ')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ml: 2}}>
                            {totalHours} <Translate expression='caregiverScreening.WorkAvailability.Summary.Hours'/>
                          </Typography>
                        </Box>
                      );
                    })
                    .filter(Boolean)
              }

              {/* Weekly total */}
              <Box
                sx={{
                  mt: 2,
                  pt: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1" fontWeight={600}>
                  <Translate expression='caregiverScreening.WorkAvailability.Summary.WeeklyTotal'/>
                </Typography>
                <Typography variant="body1" color="primary.main" fontWeight={600}>
                  {totalPossibleHours} <Translate expression='caregiverScreening.WorkAvailability.Summary.Hours'/>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
      {
        selectedCount === 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: 'warning.light',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="warning.dark">
              <Translate expression='caregiverScreening.WorkAvailability.Summary.PleaseSelectSlot'/>
            </Typography>
          </Box>
        )
      }
    </Box>
  );
};

WorkAvailabilitySection.propTypes = {
  onAvailabilityChange: PropTypes.func,
  availability: PropTypes.object,
  setValidity: PropTypes.func,
};

export default WorkAvailabilitySection;
